import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {Button, Size, Type} from '@Components/button';
import type {AJAXResponseProductData} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {ANALYTICS_ACTION_FOR_SIGNAGE, ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {GA4EventParamName} from '@Libraries/ga-events';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Text, TextSize} from '@Components/text';
import {useHashedId, usePosterProduct, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import type {PosterBookingAJAXAuthResponse, PosterBookingPlaylist, PublishingPosterbookingPanelProps} from './publishing-posterbooking-panel.types';
import {PosterBookingPanelProgressState} from './publishing-posterbooking-panel.types';
import {useAppDispatch} from '@/hooks';
import styles from './publishing-posterbooking-panel.module.scss';
import {addPosterBookingOAuthBroadcastChannel, removePosterBookingOAuthBroadcastChannel} from '@Libraries/posterbooking-library';
import {noop} from '@Utils/general.util';

export function PublishingPosterbookingPanel({...props}: PublishingPosterbookingPanelProps): ReactElement {
  const [selectedPlaylist, setSelectedPlaylist] = useState<PosterBookingPlaylist>();
  const [playlists, setPlaylists] = useState<PosterBookingPlaylist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [panelFlow, setPanelFlow] = useState<PosterBookingPanelProgressState>(PosterBookingPanelProgressState.STEP_CONNECT);
  window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_SIGNAGE, {[GA4EventParamName.TYPE]: 'PosterBooking'});
  const dispatch = useAppDispatch();
  const premiumProductDataForPoster = usePosterProduct();
  const premiumProductDataForPrint = usePrintProduct();
  const hashedId = useHashedId();
  const productUsed: AJAXResponseProductData | undefined =
    premiumProductDataForPoster && Object.keys(premiumProductDataForPoster).length > 0 ? premiumProductDataForPoster : premiumProductDataForPrint;
  const inPosterEditor = window.PMW.isReactPosterMaker() as boolean;

  const unpublishFromPosterBooking = (): void => {
    window.PMW.openConfirmationModal({
      confirmButtonText: window.i18next.t('pmwjs_unlink_pos_account'),
      hideOnClose: true,
      infoText: window.i18next.t('pmwjs_unlink_x_account_confirmation', {service: 'Posterbooking'}),
      panelTitle: window.i18next.t('pmwjs_confirmation'),
      onConfirm: () => {
        onUnlink();
      },
      onCancel: noop,
    });
  };

  const onUnlink = (): void => {
    setIsLoading(true);
    void window.PMW.writeLocal('posterbooking/unlink', {})
      .then(() => {
        setIsLoading(false);
        setPanelFlow(PosterBookingPanelProgressState.STEP_UNLINKED);
      })
      .catch(() => {
        showErrorPanel();
      });
  };
  const publishToPosterBooking = (): void => {
    if (!selectedPlaylist) {
      return;
    }
    setPanelFlow(PosterBookingPanelProgressState.STEP_LOADING);
    setIsLoading(true);

    void window.PMW.writeLocal('posterbooking/upsertDesign', {
      idPlaylist: selectedPlaylist.id,
      idPoster: hashedId,
      idOrder: productUsed?.orderId,
    })
      .then(() => {
        setIsLoading(false);
        setPanelFlow(PosterBookingPanelProgressState.STEP_PUBLISH);
      })
      .catch(() => {
        showErrorPanel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchPlaylistsForUser = (): void => {
    void window.PMW.readLocal('posterbooking/getUserPlaylistsNew', {})
      .then((res: PosterBookingPlaylist[]) => {
        if (res.length > 0) {
          setPlaylists(res);
        }
        setIsLoading(false);
      })
      .catch(() => {
        showErrorPanel();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const broadcastChannel = addPosterBookingOAuthBroadcastChannel(onOAuthSuccess, onOAuthError);

    return (): void => {
      removePosterBookingOAuthBroadcastChannel(broadcastChannel);
    };
  }, []);

  const showErrorPanel = (): void => {
    dispatch(setErrorOnPublishPanel(true));
    dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
  };

  const onOAuthSuccess = (): void => {
    setPanelFlow(PosterBookingPanelProgressState.STEP_AUTH);
    fetchPlaylistsForUser();
  };

  const onOAuthError = (): void => {
    showErrorPanel();
  };

  const initPosterbookingOAuth = (): void => {
    const oauthURL = window.PMW.util.site_url(`posterbooking/initOAuth/${hashedId}`);
    const oauthWindow: WindowProxy | null = window.open(
      '',
      'popUpWindow',
      'height=650,width=600,left=50%,top=50%,resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no, status=yes'
    );
    setIsLoading(true);
    void window.PMW.readLocal('posterbooking/checkAuthenticationStatus', {})
      .then((response: PosterBookingAJAXAuthResponse): void => {
        if (response.isAuthenticated) {
          oauthWindow?.close();
          setPanelFlow(PosterBookingPanelProgressState.STEP_AUTH);

          fetchPlaylistsForUser();
        } else if (oauthWindow) {
          oauthWindow.location.href = oauthURL;
        }
      })
      .catch(() => {
        showErrorPanel();
        oauthWindow?.close();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const posterbookingAuthenticatedPanel = (): ReactElement => {
    return (
      <div>
        <div className={`${inPosterEditor ? styles.playlistDisplayHeightForDropDown : styles.playlistDisplayHeightForPanel} spacing-m-b-6 _overflow-auto`}>
          {playlists.map((playlist) => {
            return (
              <PublishingOptionListItem
                customThumbnailClasses={`${selectedPlaylist?.id === playlist.id ? 'bg-primary-10 content-primary' : 'bg-neutral-3 content-body'} `}
                iconName="icon-video-in-page"
                heading={playlist.name}
                isClicked={selectedPlaylist?.id === playlist.id}
                thumbNailType={ThumbNailType.ICON}
                onClick={() => {
                  setSelectedPlaylist(playlist);
                }}
              />
            );
          })}
        </div>
        <div className={inPosterEditor ? 'flex-v-row' : 'flex-h-row'}>
          <Button
            onClick={publishToPosterBooking}
            size={inPosterEditor ? Size.SMALL : Size.MEDIUM}
            customClasses={inPosterEditor ? '' : 'flex-1 spacing-m-r-1'}
            text={window.i18next.t('pmwjs_publish')}
            type={Type.PRIMARY}
            isFullWidth={inPosterEditor}
            disabled={!selectedPlaylist}
          />
          <Button
            onClick={unpublishFromPosterBooking}
            customClasses={inPosterEditor ? 'spacing-m-t-2' : 'flex-1 spacing-m-l-1'}
            icon="icon-unlink"
            size={inPosterEditor ? Size.SMALL : Size.MEDIUM}
            text={window.i18next.t('pmwjs_unlink_account')}
            isFullWidth={inPosterEditor}
            type={Type.SECONDARY}
          />
        </div>
      </div>
    );
  };

  const buttonForDone = (): ReactElement => {
    return <Button isFullWidth type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_done')} onClick={props.onClose} />;
  };
  const getDisplayPanel = (): ReactElement => {
    switch (panelFlow) {
      case PosterBookingPanelProgressState.STEP_CONNECT:
        return (
          <DigitalSignageSubPanel
            panelHeading={window.i18next.t('pmwjs_posterbooking')}
            heading={window.i18next.t('pmwjs_posterbooking_signage')}
            subheading={window.i18next.t('pmwjs_posterbooking_signage_desc')}
            showButton
            buttonText={window.i18next.t('pmwjs_posterbooking_connect')}
            onClick={initPosterbookingOAuth}
            onClose={props.onClose}
            imgSrc="publishing-poster-booking.png"
          />
        );
      case PosterBookingPanelProgressState.STEP_AUTH:
        return (
          <DigitalSignageSubPanel
            panelHeading={window.i18next.t('pmwjs_posterbooking')}
            heading={window.i18next.t('pmwjs_posterbooking_select_list')}
            listItem={posterbookingAuthenticatedPanel()}
            onClose={props.onClose}
          />
        );
      case PosterBookingPanelProgressState.STEP_PUBLISH:
        return (
          <DigitalSignageSubPanel
            illustration="publishing-dripboards-screens.png"
            headerClassName="flex-center"
            panelHeading={window.i18next.t('pmwjs_published')}
            heading=""
            listItem={
              <div className="flex-center flex-v-row">
                <Text dangerouslySetInnerHTML size={TextSize.SMALL} className="content-body spacing-m-b-4" val={window.i18next.t('pmwjs_posterbooking_success_message')} />
                <Text size={TextSize.XSMALL} className="content-sub-text" val={window.i18next.t('pmwjs_posterbooking_success_desc')} />
              </div>
            }
            showButton
            buttonComponent={buttonForDone()}
            onClose={props.onClose}
          />
        );
      case PosterBookingPanelProgressState.STEP_UNLINKED:
        return (
          <DigitalSignageSubPanel
            headerClassName="flex-center"
            panelHeading={window.i18next.t('pmwjs_unlinked')}
            heading=""
            listItem={
              <div className="flex-center">
                <Text size={TextSize.SMALL} className="content-content-body" val={window.i18next.t('pmwjs_account_unlinked')} />
              </div>
            }
            showButton
            buttonComponent={buttonForDone()}
            onClose={props.onClose}
          />
        );
      default:
        return <div />;
    }
  };

  const getLoader = (): ReactElement => {
    return (
      <div className={`${styles.setCustomHeight} flex-v-row flex-center`}>
        <CircularProgressLoader size={LOADER_SIZE.LARGE} classes="spacing-m-0" />
        {panelFlow === PosterBookingPanelProgressState.STEP_LOADING ? (
          <Text dangerouslySetInnerHTML className="content-sub-text spacing-m-t-6" size={TextSize.SMALL} val={window.i18next.t('pmwjs_uploading_posterbooking')} />
        ) : null}
      </div>
    );
  };

  return isLoading ? getLoader() : getDisplayPanel();
}
